import type { CarrierAgreementPenaltyRow } from '../../../store/api/carrierAgreementsApi';
import type { RouteExpense } from '../../../store/api/financeApi';
import dayjs from 'dayjs';
import {
  mapFormValuesToCreatePayload,
  mapRouteExpenseToFormValues,
  mergeRouteExpenseFormValues,
  type RouteExpenseFormValues
} from '../../finance/domain/routeExpenseFormValues';
import type {
  CompletionExpenseDraft,
  CompletionPenaltyDraft,
  CompletionReadinessState,
  CompletionValidationState,
  RouteCompletionDraft
} from '../types';

export type { CompletionExpenseDraft };

export function mapRouteExpenseToDraft(e: RouteExpense): CompletionExpenseDraft {
  const base = mapRouteExpenseToFormValues(e);
  return {
    ...base,
    expenseId: e.id,
    status: e.status ?? 'DRAFT',
    isNew: false
  };
}

export function createEmptyExpenseDraft(): CompletionExpenseDraft {
  return {
    ...mergeRouteExpenseFormValues({ expenseDate: dayjs() }),
    status: 'DRAFT',
    isNew: true
  };
}

export function patchCompletionExpenses(
  draft: RouteCompletionDraft,
  expenses: CompletionExpenseDraft[]
): RouteCompletionDraft {
  return revalidateDraft({ ...draft, expenses });
}

export function patchCompletionExpense(
  draft: RouteCompletionDraft,
  index: number,
  patch: Partial<CompletionExpenseDraft>
): RouteCompletionDraft {
  const expenses = draft.expenses.map((e, i) => (i === index ? { ...e, ...patch } : e));
  return revalidateDraft({ ...draft, expenses });
}

export function penaltyConfigKey(rows: CarrierAgreementPenaltyRow[]): string {
  return rows
    .filter((p) => p.requiresManualInput)
    .map((p) => p.id)
    .sort((a, b) => a - b)
    .join(',');
}

export function mapPenaltyConfigToDraft(row: CarrierAgreementPenaltyRow): CompletionPenaltyDraft {
  const typeU = String(row.type).toUpperCase();
  return {
    penaltyId: row.id,
    code: row.code,
    name: row.name,
    enabled: false,
    penaltyType: typeU,
    perDay: row.perDay,
    requiresComment: Boolean(row.requiresComment),
    days: null,
    amount: null,
    percent: typeU === 'PERCENT' ? Number(row.value) || null : null,
    comment: ''
  };
}

export function buildInitialCompletionDraft(params: {
  penaltyConfigs: CarrierAgreementPenaltyRow[];
  expenses?: RouteExpense[];
}): RouteCompletionDraft {
  const penalties = params.penaltyConfigs
    .filter((p) => p.requiresManualInput)
    .map(mapPenaltyConfigToDraft);

  const expenses: CompletionExpenseDraft[] = (params.expenses ?? []).map(mapRouteExpenseToDraft);

  const validationState = validateCompletionDraft({
    logistComment: '',
    penalties,
    expenses,
    warnings: [],
    readinessState: 'not_applicable',
    validationState: 'incomplete'
  }).validationState;

  return {
    logistComment: '',
    penalties,
    expenses,
    warnings: [],
    readinessState: 'not_applicable',
    validationState
  };
}

export function patchCompletionPenalty(
  draft: RouteCompletionDraft,
  penaltyId: number,
  patch: Partial<Pick<CompletionPenaltyDraft, 'enabled' | 'days' | 'amount' | 'percent' | 'comment'>>
): RouteCompletionDraft {
  const penalties = draft.penalties.map((p) =>
    p.penaltyId === penaltyId ? { ...p, ...patch } : p
  );
  return revalidateDraft({ ...draft, penalties });
}

export function patchCompletionLogistComment(
  draft: RouteCompletionDraft,
  logistComment: string
): RouteCompletionDraft {
  return revalidateDraft({ ...draft, logistComment });
}

function revalidateDraft(draft: RouteCompletionDraft): RouteCompletionDraft {
  const result = validateCompletionDraft(draft);
  return {
    ...draft,
    validationState: result.validationState,
    warnings: result.warnings,
    readinessState: result.readinessState
  };
}

export function validateCompletionDraft(draft: RouteCompletionDraft): {
  validationState: CompletionValidationState;
  readinessState: CompletionReadinessState;
  warnings: RouteCompletionDraft['warnings'];
  blockers: { code: string; message: string }[];
} {
  const warnings: RouteCompletionDraft['warnings'] = [];
  const blockers: { code: string; message: string }[] = [];

  for (const p of draft.penalties) {
    if (!p.enabled) continue;
    if (p.perDay && (p.days == null || p.days < 1)) {
      blockers.push({
        code: `penalty_days_${p.code}`,
        message: `${p.name}: укажите количество дней`
      });
    }
    if (p.penaltyType === 'FIXED' && (p.amount == null || p.amount < 0)) {
      blockers.push({
        code: `penalty_amount_${p.code}`,
        message: `${p.name}: укажите сумму`
      });
    }
    if (p.requiresComment && !p.comment?.trim()) {
      warnings.push({
        code: `penalty_comment_${p.code}`,
        message: `${p.name}: рекомендуется комментарий`,
        severity: 'info'
      });
    }
  }

  for (const ex of draft.expenses) {
    if (!ex.isNew) continue;
    if (ex.amountWithVAT == null || ex.amountWithVAT <= 0) {
      blockers.push({
        code: 'expense_amount',
        message: 'Укажите сумму по каждому новому расходу'
      });
    }
  }

  const hasEnabledPenalty = draft.penalties.some((p) => p.enabled);
  if (!draft.logistComment.trim() && !hasEnabledPenalty) {
    warnings.push({
      code: 'logist_comment_empty',
      message: 'Комментарий логиста не заполнен',
      severity: 'info'
    });
  }

  const validationState: CompletionValidationState =
    blockers.length > 0 ? 'invalid' : draft.penalties.length === 0 && !draft.logistComment.trim()
      ? 'incomplete'
      : 'valid';

  const readinessState: CompletionReadinessState =
    blockers.length > 0 ? 'blocked' : warnings.length > 0 ? 'warning' : 'ready';

  return { validationState, readinessState, warnings, blockers };
}

export function completionDraftToExpenseCreates(
  draft: RouteCompletionDraft,
  routeId: number
): Array<{ routeId: number; data: ReturnType<typeof mapFormValuesToCreatePayload> }> {
  return draft.expenses
    .filter((e) => e.isNew && e.amountWithVAT != null && e.amountWithVAT > 0)
    .map((e) => ({
      routeId,
      data: mapFormValuesToCreatePayload(e as RouteExpenseFormValues)
    }));
}

/** API payload for enabled penalties only. */
export function completionDraftToPenaltyCreates(
  draft: RouteCompletionDraft,
  routeId: number
): Array<{
  routeId: number;
  agreementPenaltyId: number;
  days?: number;
  comment?: string;
  amount?: number;
}> {
  return draft.penalties
    .filter((p) => p.enabled)
    .map((p) => ({
      routeId,
      agreementPenaltyId: p.penaltyId,
      days: p.perDay ? Number(p.days) : undefined,
      comment: (p.comment || draft.logistComment).trim() || undefined,
      amount: p.penaltyType === 'FIXED' && p.amount != null ? Number(p.amount) : undefined
    }));
}
